import { FullSingleLayerStack } from 'domains/viewer/ViewportsConfigurations';
import { AnalyticEventName } from 'modules/analytics/analytics';
import { Json } from 'types';
import { SupportedTextureTypes, SupportedTexturesMap } from 'utils/textureUtils';
import { CacheStrategy } from '../PixelCacheStategy';
import type { FrameData } from 'modules/viewer/imageloaders/BaseWebSocketImageLoader';
import Deferred from 'utils/deferred';
import { GetStackSignedUrlsQuery, ModalityLutModule, VoiLutModule } from 'generated/graphql';
import { ImageLoadMetricSummary } from './LoadMetricsCollector';
import { ExtWindowName } from 'hooks/useExtensionState';

export enum PixelDataSharedWorkerError {
  OutOfMemory = 'Pixel Data OOM Transfer Error',
  NotEnoughMemory = 'Cannot fully load selected stacks. Reduce layout size and try again.',
  FrameNotFound = 'Frame not found',
  FailedToFetch = 'Failed to fetch pixel data from cache or DCMData',
}

export const cancelled = 'cancelled';

export type LoadCompleteMessage = {
  type: 'load-complete';
  stackSmid: string;
};
export type LoadErrorMessage = {
  type: 'load-error';
  stackSmid: string;
  error: string;
};
export type FrameMessageProto = {
  frameSmid: string;
  stackSmid: string;
  pixels: SupportedTextureTypes;
  range: [number, number];
  fromCache: boolean;
};
export type HandleFrameMessage = FrameMessageProto & {
  type: 'handle-frame';
};
export type HandleInitialFrameMessage = FrameMessageProto & {
  type: 'handle-initial-frame';
  volume?: SupportedTextureTypes;
};
export type HandleCacheOperationMessage = {
  framesProcessed: number;
  transferType: 'write' | 'read';
  estimatedCacheSize?: number;
};
export type AnalyticsErrorMessage = {
  type: 'analytics-error';
  error: Error;
  data?: Json;
};

export type AnalyticsReportMessage = {
  type: 'analytics-report';
  data: Partial<ImageLoadMetricSummary>;
};

export type AnalyticsTrackMessage = {
  type: 'analytics-track';
  name: AnalyticEventName;
  data?: Json;
};

export type TransferCancelledMessage = {
  type: 'transfer-cancelled';
};

export type PongMessage = {
  type: 'pong';
  inits: number;
};

export type WorkerSingleMessageData =
  | PongMessage
  | LoadCompleteMessage
  | LoadErrorMessage
  | HandleFrameMessage
  | HandleInitialFrameMessage
  | AnalyticsErrorMessage
  | AnalyticsReportMessage
  | AnalyticsTrackMessage
  | TransferCancelledMessage;

export type BatchedMessages = {
  type: 'batched-messages';
  messages: WorkerSingleMessageData[];
};

export type WorkerMessageData = WorkerSingleMessageData | BatchedMessages;

export type InitMessage = {
  type: 'init';
  transferType: TransferType;
  SUPPORTED_TEXTURES: SupportedTexturesMap;
  strategy: CacheStrategy;
  /** Window name of the caller */
  windowName: ExtWindowName | null;
};

export type PingMessage = {
  type: 'ping';
};

export type LoadStackMessage = {
  type: 'load-stack';
  stack: FullSingleLayerStack;
  initialSlice: number;
  partOfInitialLoad: boolean;
  isDropped: boolean;
  priority: number;
  /** Window name of the caller */
  windowName: ExtWindowName | null;
};

export type LoadFrameMessage = {
  type: 'load-frame';
  stack: FullSingleLayerStack;
  frameIndex: number;
  priority: number;
  /** Window name of the caller */
  windowName: ExtWindowName | null;
};

export type UpdatePriorityMessage = {
  type: 'update-priority';
  stack: FullSingleLayerStack;
  focus: number;
  isDropped: boolean;
  priority: number;
};

export type CancelTransferMessage = {
  type: 'cancel-transfer';
  stack?: FullSingleLayerStack;
};

export type ClearCacheMessage = {
  type: 'clear-cache';
};

export type InboundMessageData =
  | PingMessage
  | LoadStackMessage
  | LoadFrameMessage
  | InitMessage
  | UpdatePriorityMessage
  | CancelTransferMessage
  | ClearCacheMessage;

export type TransferStartingMessage = {
  event: 'transfer-starting';
  requestId: string;
};

export type TransferErrorOutgoingMessage = {
  event: 'transfer-error';
  stacks: string[];
  error: string;
};

export type TransferCompleteMessage = {
  event: 'transfer-complete';
  requestId: string;
  framesTransfered: number;
  endingPriority: number;
  endingFocus: number;
};

export type FrameDataMessage = {
  event: 'frame-data';
  cachePixelUrl: string;
  dcmdataPixelUrl: string | null | undefined;
};

export type OutgoingMessage =
  | TransferStartingMessage
  | TransferErrorOutgoingMessage
  | TransferCompleteMessage
  | FrameDataMessage;

export type DataReceivedMessage = {
  event: 'data-received';
  frameSmid: string;
  pixels: SupportedTextureTypes;
};

export type CallbackMessage = OutgoingMessage | DataReceivedMessage;

export type MessageReceivedCallback = (message: CallbackMessage) => void;

export type DataRequest = {
  stack: FullSingleLayerStack;
  focus: number;
  partOfInitialLoad: boolean;
  isDropped: boolean;
  processing: boolean;
  priority: number;
  messageQueue: Array<WorkerMessageData>;
  windowName: ExtWindowName | null;
};

export type DedupedDataRequest = DataRequest & {
  connectionId: string;
};

export type HandleMessage = (event: MessageEvent) => void;

export const REMAINING_FRAMES_TIMEOUT = 250;
export const MAX_PAYLOAD = 500 * 1024 * 1024; // 500 MB

export type LoaderCallbackArg =
  | {
      stackSmid: string;
      type: 'initial-frame' | 'frame' | 'complete';
      data: HandleFrameMessage | HandleInitialFrameMessage | FrameData | null;
    }
  | {
      stackSmid: string;
      type: 'cache-transfer-complete';
      data: HandleCacheOperationMessage;
    }
  | {
      stackSmid: string;
      type: 'error';
      data: string;
    };

export type WSTransferType = 'url-pixels';
export type TransferProtocol = 'http' | 'ws';
export type TransferType = WSTransferType | 'http';

export type LoaderCallback = (arg: LoaderCallbackArg) => void;

export type CacheObject = {
  stackSmid: string;
  frameSmid: string;
  data: {
    pixels: SupportedTextureTypes;
  };
};

export type PixelMapEntryState = 'received' | 'written';
export type PixelMapEntry = {
  frameInfo: OrderedFrame;
  data: {
    pixels: SupportedTextureTypes;
  };
  status: PixelMapEntryState;
  size: number;
  writeId?: string;
};

export type PixelMap = Map<string, PixelMapEntry>;

export type FrameResponse = ArrayBuffer | null | typeof cancelled;

export type QueuedFrame = {
  stackSmid: string;
  url: string;
  includeCredentials: boolean;
  frameSmid: string;
  sortIndex: number;
  deferred: Deferred<FrameResponse>;
  priority: number;
  expirationTime?: number;
};

export type DedupedQueuedFrame = QueuedFrame & {
  pixelUrl: GetStackSignedUrlsQuery['pixelURLs'][number];
  abortSignal?: AbortSignal;
};

export type LoadRequest = {
  orderedFrames: ReadonlyArray<OrderedFrame>;
  priority: number;
  focus: number;
  stackSmid: string;
};

export type OrderedFrame = {
  seriesSmid: string;
  frameSmid: string;
  stackSmid: string;
  isMultiFrame: boolean;
  sortIndex: number;
  modules: {
    modalityLut?: ModalityLutModule | null;
    voiLut?: VoiLutModule | null;
  };
};

// map of stack smid to sets of failed frame smids
export type DownloadStacksResult = Map<string, Set<string>>;

/** Map of stackSmids to window names. Used for metrics attribution */
export type StackWindowNameMap = Map<string, Set<string | null>>;
